import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import md5 from 'md5';
import { assoc, dissoc, has, isEmpty, isNil, reject } from '../../utils/fp';
import { CHANGE_CONSTRAINTS, CHANGE_TERM, RESET_SEARCH } from '../../ducks/search';
import { getConstraints, getFacet } from '../../selectors/router';
import { useVisNavigate } from '../useVisNavigate';

// A new term or a new facet value invalidates the result set: the page, the
// facet and the previous constraints go with it.
const searchReset = {
  start: 0,
  constraints: null,
  facet: null,
  highlightedConstraints: null,
};

export const useSearchNavigation = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useVisNavigate();

  return useMemo(
    () => ({
      resetSearch: () => {
        dispatch({ type: RESET_SEARCH });
        // no payload: back to the home page with the invariants only
        navigate.push('/');
      },
      changeTerm: ({ term } = {}) => {
        const payload = { ...searchReset, term };
        dispatch({ type: CHANGE_TERM, payload });
        navigate.push('/', payload);
      },
      changeFacet: facetId => {
        const prevFacetId = getFacet(store.getState());
        navigate.push('/', { facet: facetId === prevFacetId ? null : facetId });
      },
      changeStart: start => navigate.push('/', { start }),
      changeConstraints: (facetId, constraintIds, { bypass } = {}) => {
        // constraintIds can be an array because of Chips component or a string because of CollapseButtons
        const constraintId = Array.isArray(constraintIds) ? constraintIds[0] : constraintIds;
        const prevConstraints = getConstraints(store.getState());
        let constraints;
        if (isNil(facetId)) {
          // clear all constraints
          constraints = {};
        } else if (isEmpty(constraintIds)) {
          // clear all values of a facet
          constraints = reject(constraint => constraint.facetId === facetId, prevConstraints);
        } else {
          // add or remove a value in a facet
          const hash = md5(`${facetId}${constraintId}`);
          constraints = has(hash, prevConstraints)
            ? dissoc(hash, prevConstraints)
            : assoc(hash, { facetId, constraintId }, prevConstraints);
        }

        const payload = { constraints, start: 0, facet: facetId, bypass, searchResultNb: null };
        dispatch({ type: CHANGE_CONSTRAINTS, payload });
        navigate.push('/', payload);
      },
      changeSearchOrder: index => navigate.push('/', { sortIndexSelected: index }),
      // the result count is an answer of the search api, not a user intent: it
      // is written to the url without stacking a history entry
      setSearchResultNb: searchResultNb => navigate.replace('/', { searchResultNb }),
    }),
    [dispatch, navigate, store],
  );
};
